import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPromisesToPay } from '../../../services/loanService';
import type { CollectionNote } from '../../../services/schemas/loanServiceSchemas/collectionNote';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';
import { PROMISE_STATUS_COLORS, contactMethodLabel, promiseStatusLabel } from '../../../constants/collectionFollowUp';

type PromiseView = 'today' | 'broken' | 'upcoming';

interface PromisesToPayPanelProps {
  /** Customer names by ledger account id, from the overdue list, so a promise shows who made it. */
  customerNames?: Record<string, string>;
}

/**
 * US-36: the promises to pay the collections team must follow up - due today, broken, and still to come.
 * Statuses are re-checked against payments by the backend each time the list loads.
 */
const PromisesToPayPanel: React.FC<PromisesToPayPanelProps> = ({ customerNames = {} }) => {
  const navigate = useNavigate();
  const [promises, setPromises] = useState<CollectionNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [view, setView] = useState<PromiseView>('today');
  const today = toLocalDateString();

  const load = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      // No status filter: the backend returns pending and broken promises
      const response = await getPromisesToPay();
      setPromises(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load promises to pay:', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const pending = promises.filter((p) => p.promiseStatus === 'PENDING');
    return {
      today: pending.filter((p) => (p.promiseDate || '') <= today),
      upcoming: pending.filter((p) => (p.promiseDate || '') > today),
      // Most recently broken first: those are the freshest to chase
      broken: promises.filter((p) => p.promiseStatus === 'BROKEN').slice().reverse(),
    };
  }, [promises, today]);

  const rows = groups[view];

  const tabs: { key: PromiseView; label: string; icon: string; color: string }[] = [
    { key: 'today', label: 'Due today', icon: 'fa-calendar-check-o', color: 'warning' },
    { key: 'broken', label: 'Broken', icon: 'fa-chain-broken', color: 'danger' },
    { key: 'upcoming', label: 'Upcoming', icon: 'fa-clock-o', color: 'primary' },
  ];

  return (
    <div className="card mb-3">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h6 className="card-title mb-0">
            <i className="fa fa-handshake-o me-2 text-primary"></i>
            Promises to pay
          </h6>
          <small className="text-muted">Recorded when a contact is logged on the loan account; kept or broken is worked out from the payments received.</small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`btn btn-sm ${view === tab.key ? `btn-${tab.color}` : `btn-outline-${tab.color}`}`}
              onClick={() => setView(tab.key)}
            >
              <i className={`fa ${tab.icon} me-1`}></i>
              {tab.label}
              <span className="badge bg-light text-dark ms-2">{groups[tab.key].length}</span>
            </button>
          ))}
          <button type="button" className="btn btn-sm btn-light" onClick={load} disabled={isLoading} title="Refresh">
            <i className={`fa fa-refresh ${isLoading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        {isError ? (
          <p className="text-muted text-center py-4 mb-0">
            <i className="fa fa-exclamation-triangle text-warning me-2"></i>
            Could not load promises to pay.
          </p>
        ) : isLoading && promises.length === 0 ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted text-center py-4 mb-0">
            {view === 'today' && 'No promises due today.'}
            {view === 'broken' && 'No broken promises.'}
            {view === 'upcoming' && 'No promises coming up.'}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Customer / account</th>
                  <th className="text-end">Promised</th>
                  <th>Pay by</th>
                  <th className="text-end">Paid so far</th>
                  <th>Status</th>
                  <th>Agreed with</th>
                  <th>Logged by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const name = customerNames[p.accountId || ''];
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="fw-semibold">{name || p.accountNumber || 'Loan account'}</div>
                        {name && p.accountNumber && <small className="text-muted">{p.accountNumber}</small>}
                      </td>
                      <td className="text-end fw-semibold">{formatCurrency(p.promiseAmount || 0)}</td>
                      <td>{formatDate(p.promiseDate)}</td>
                      <td className="text-end">{formatCurrency(p.promisePaidAmount || 0)}</td>
                      <td>
                        <span className={`badge bg-${PROMISE_STATUS_COLORS[p.promiseStatus || ''] || 'secondary'}`}>
                          {promiseStatusLabel(p.promiseStatus)}
                        </span>
                      </td>
                      <td>
                        <div className="small">{p.contactPerson || '-'}</div>
                        {p.contactMethod && <small className="text-muted">{contactMethodLabel(p.contactMethod)}</small>}
                      </td>
                      <td>
                        <div className="small">{p.createdBy}</div>
                        <small className="text-muted">{formatDate(p.createdDate)}</small>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/loan-accounts/${p.accountId}`)}
                        >
                          <i className="fa fa-eye me-1"></i>Account
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromisesToPayPanel;
